import { motion, AnimatePresence } from "framer-motion";
import { Users, Briefcase, MessageCircle, DollarSign, Settings } from "lucide-react";
import { useState, useEffect } from "react";
import ImpactLogo from "../../assets/images/Impact_Logo.png";

const segments = [
  {
    label: "Governance",
    icon: Settings,
    color: "#99246A",
    gradient: "url(#seg1)",
    startAngle: 270,
    endAngle: 342,
    desc: "Oversight, compliance, and strategic direction."
  },
  {
    label: "People Management",
    icon: Users,
    color: "#16A7C4",
    gradient: "url(#seg2)",
    startAngle: 342,
    endAngle: 54,
    desc: "Talent, leadership, and team culture."
  },
  {
    label: "Communication & Partnerships",
    icon: MessageCircle,
    color: "#99246A",
    gradient: "url(#seg3)",
    startAngle: 54,
    endAngle: 126,
    desc: "Stakeholder engagement and collaboration."
  },
  {
    label: "Finance & Fundraising",
    icon: DollarSign,
    color: "#16A7C4",
    gradient: "url(#seg4)",
    startAngle: 126,
    endAngle: 198,
    desc: "Resource mobilization and sustainability."
  },
  {
    label: "Program & Operations",
    icon: Briefcase,
    color: "#99246A",
    gradient: "url(#seg5)",
    startAngle: 198,
    endAngle: 270,
    desc: "Execution, delivery, and impact tracking."
  },
];

const center = 240;
const outerRadius = 200;
const innerRadius = 90;
const iconRadius = 145;
const labelRadius = 230;

function polarToCartesian(cx, cy, r, angle) {
  const a = ((angle - 90) * Math.PI) / 180.0;
  return {
    x: cx + r * Math.cos(a),
    y: cy + r * Math.sin(a),
  };
}

function describeArc(cx, cy, r1, r2, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, r1, endAngle);
  const endOuter = polarToCartesian(cx, cy, r1, startAngle);
  const startInner = polarToCartesian(cx, cy, r2, endAngle);
  const endInner = polarToCartesian(cx, cy, r2, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", startOuter.x, startOuter.y,
    "A", r1, r1, 0, largeArcFlag, 0, endOuter.x, endOuter.y,
    "L", endInner.x, endInner.y,
    "A", r2, r2, 0, largeArcFlag, 1, startInner.x, startInner.y,
    "Z"
  ].join(" ");
}

const AUTO_HIGHLIGHT_DURATION = 1200; // ms per segment

const ImpactFramework = () => {
  const [hovered, setHovered] = useState(null);
  const [autoIdx, setAutoIdx] = useState(0);
  const [autoActive, setAutoActive] = useState(true);

  // Auto-highlight animation on mount
  useEffect(() => {
    if (!autoActive) return;
    const timer = setTimeout(() => setAutoIdx(idx => (idx + 1) % segments.length), AUTO_HIGHLIGHT_DURATION);
    return () => clearTimeout(timer);
  }, [autoIdx, autoActive]);

  // When user hovers, stop auto animation
  const handleMouseEnter = (label) => {
    setAutoActive(false);
    setHovered(label);
  };
  const handleMouseLeave = () => setHovered(null);

  // Which segment is highlighted?
  const highlighted = autoActive ? segments[autoIdx].label : hovered;

  return (
    <section className="py-8 bg-gradient-to-bl from-white to-blue-50 overflow-x-hidden px-4">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Left: Animated Donut Diagram */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="flex justify-center w-full"
        >
          <div className="w-full max-w-[90vw] md:max-w-[480px] mx-auto">
            <svg
              viewBox="0 0 480 480"
              width="100%"
              className="block"
            >
            {/* Gradients for segments */}
            <defs>
              <linearGradient id="seg1" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#99246A" />
                <stop offset="100%" stopColor="#16A7C4" />
              </linearGradient>
              <linearGradient id="seg2" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16A7C4" />
                <stop offset="100%" stopColor="#99246A" />
              </linearGradient>
              <linearGradient id="seg3" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#99246A" />
                <stop offset="100%" stopColor="#16A7C4" />
              </linearGradient>
              <linearGradient id="seg4" x1="1" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#16A7C4" />
                <stop offset="100%" stopColor="#99246A" />
              </linearGradient>
              <linearGradient id="seg5" x1="0.5" y1="0" x2="0.5" y2="1">
                <stop offset="0%" stopColor="#99246A" />
                <stop offset="100%" stopColor="#16A7C4" />
              </linearGradient>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {/* Donut Segments */}
            {segments.map((seg, i) => {
              let end = seg.endAngle;
              if (end <= seg.startAngle) end += 360;
              const path = describeArc(center, center, outerRadius, innerRadius, seg.startAngle, end);
              const midAngle = (seg.startAngle + (end - seg.startAngle) / 2);
              const iconPos = polarToCartesian(center, center, iconRadius, midAngle);
              const labelPos = polarToCartesian(center, center, labelRadius, midAngle);
              // Label position logic: always outside donut, never cut off
              let labelX = labelPos.x;
              let labelY = labelPos.y;
              let anchor = "middle";
              if (midAngle > 60 && midAngle < 120) anchor = "start";
              if (midAngle > 240 && midAngle < 300) anchor = "end";
              // Clamp label position to keep it inside SVG
              const labelBoxWidth = 160;
              const labelBoxHeight = 40;
              const svgSize = 480;
              const clampedX = Math.max(0, Math.min(labelX - labelBoxWidth / 2, svgSize - labelBoxWidth));
              const clampedY = Math.max(0, Math.min(labelY - labelBoxHeight, svgSize - labelBoxHeight));
              return (
                <motion.g
                  key={seg.label}
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 120 }}
                  onMouseEnter={() => handleMouseEnter(seg.label)}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: 'pointer' }}
                >
                  <motion.path
                    d={path}
                    fill={seg.gradient}
                    opacity={highlighted === seg.label ? 0.32 : 0.18}
                    stroke={seg.color}
                    strokeWidth={highlighted === seg.label ? 5 : 2}
                    filter={highlighted === seg.label ? 'url(#glow)' : ''}
                    transition={{ duration: 0.2 }}
                  />
                  <foreignObject x={iconPos.x - 28} y={iconPos.y - 28} width={56} height={56} className="flex items-center justify-center">
                    <motion.div
                      className="flex items-center justify-center h-full w-full rounded-full bg-white/80 shadow-lg"
                      animate={highlighted === seg.label ? { scale: 1.18 } : { scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <seg.icon className="w-8 h-8 text-primary" />
                    </motion.div>
                  </foreignObject>
                  {/* Show label only if highlighted (auto or hover) */}
                  <AnimatePresence>
                    {highlighted === seg.label && (
                      <motion.g
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <foreignObject x={clampedX} y={clampedY} width={labelBoxWidth} height={labelBoxHeight}>
                          <div className="bg-white text-primary text-sm font-semibold rounded-lg shadow-lg px-2 py-2 border border-primary/30 text-center pointer-events-none select-none whitespace-nowrap">
                            {seg.label}
                          </div>
                        </foreignObject>
                      </motion.g>
                    )}
                  </AnimatePresence>
                </motion.g>
              );
            })}
            {/* Center logo */}
            <motion.circle
              cx={center}
              cy={center}
              r={innerRadius - 10}
              fill="#fff"
              stroke="#99246A"
              strokeWidth={7}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
              className="drop-shadow-2xl"
            />
            <image
              href={ImpactLogo}
              x={center - 64}
              y={center - 48}
              width={120}
              height={96}
              style={{ pointerEvents: 'none' }}
            />
          </svg>
          </div>
        </motion.div>

        {/* Right: Explanation */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
          className="text-left"
        >
          <h2 className="text-3xl font-heading font-bold text-primary mb-6">
            Impact Together Framework
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            We concentrate on reinforcing the five critical components of an
            intervention, closely aligning with the influencer's vision and
            mission.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed">
            Through this framework, we engage partners to become impactful
            contributors in the realm of impact investing.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactFramework;
